import React, { useState, useEffect } from "react";
import {
  Layout,
  Menu,
  theme,
  Carousel,
  Tabs,
  message,
  Spin,
  Modal,
  Button,
  Input,
  Form,
  Drawer,
  Avatar,
  Space,
  Typography,
  Grid,
  Flex,
  Empty,
} from "antd";
import {
  MenuOutlined,
} from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { osApi } from "../services/osApi";

const { Header, Content, Footer, Sider } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

interface Partner {
  id: number;
  category: string;
  logo: string;
  url: string;
}

interface Platform {
  id: string;
  url: string;
  type: string;
}

interface CompanyInfo {
  name: string;
  logo: string;
}

interface MainData {
  bannerImages: string[];
  mobileContentImages: string[];
  videoUrl: string;
  partners: Partner[];
}

interface FooterData {
  serviceDescription: string;
  platforms: Platform[];
}

interface LayoutProps {
  children: React.ReactNode;
  companyInfo: CompanyInfo;
  mainData: MainData;
  footerData: FooterData;
  loading: boolean;
  mode?: "default" | "no-banner" | "fullscreen";
}

// 注意：現在使用統一配置文件中的常量

const getYoutubeVideoId = (url: string) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

export default function AppLayout({
  children,
  companyInfo,
  mainData,
  footerData,
  loading,
  mode = "default",
}: LayoutProps): JSX.Element {
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [activeRegion, setActiveRegion] = useState<string>("");
  const [loginModalVisible, setLoginModalVisible] = useState<boolean>(false);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [selectedKey, setSelectedKey] = useState<string>("home");

  // 登入驗證相關狀態
  const [loginLoading, setLoginLoading] = useState<boolean>(false);

  // 🎯 添加訂單編號 state 管理
  const [projectNameValue, setProjectNameValue] = useState<string>("");

  // 使用 Antd 的 useBreakpoint hook
  const screens = useBreakpoint();
  const isDesktop = screens.md; // md 斷點及以上為桌面版
  const isMobile = !screens.md; // md 斷點以下為移動版

  // 使用 useLocation hook 來監聽路由變化
  const location = useLocation();

  // 使用 queryClient 來手動查詢
  const queryClient = useQueryClient();

  // 根據 mode 獲取主內容區域的樣式
  const getContentStyles = () => {
    const baseStyles = {
      ...(isMobile && mode !== "fullscreen" && { paddingTop: "80px" }), // 移動端頂部間距，全屏模式除外
    };

    switch (mode) {
      case "fullscreen":
        return {
          margin: "0", // 全屏模式：去除所有間距，占滿整個屏幕
          padding: "0",
        };
      case "no-banner":
        return {
          ...baseStyles,
          margin: "0", // 直播頁面去除 margin，讓內容充滿屏幕
        };
      case "default":
      default:
        return {
          ...baseStyles,
          margin: "24px 16px", // 默認頁面保持原有間距
        };
    }
  };

  // 根據當前 URL 獲取對應的 menu key
  const getCurrentMenuKey = () => {
    if (typeof window === "undefined") return "home";

    const pathname = window.location.pathname;
    switch (pathname) {
      case "/":
        return "home";
      case "/about":
        return "about";
      case "/news":
        return "news";
      case "/combo":
        return "combo";
      case "/join":
        return "join";
      case "/member":
      case "/livestream":
      case "/interactive":
        return "member";
      default:
        return "home";
    }
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const { bannerImages, partners } = mainData;
  const { serviceDescription, platforms } = footerData;

  useEffect(() => {
    if (partners.length > 0) {
      setActiveRegion(partners[0].category);
    }
  }, [partners]);

  // 檢查登入狀態
  useEffect(() => {
    const memberInfo = localStorage.getItem("memberInfo");
    setIsLoggedIn(!!memberInfo);
  }, []);

  // 設置初始 selectedKey 和監聽 URL 變化
  useEffect(() => {
    const currentKey = getCurrentMenuKey();
    setSelectedKey(currentKey);
  }, [location.pathname]); // 改為監聽 location.pathname 變化

  // 當切換到桌面版時自動關閉移動端菜單
  useEffect(() => {
    if (isDesktop && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  }, [isDesktop, mobileMenuOpen]);

  // 處理登出
  const handleLogout = () => {
    localStorage.removeItem("memberInfo");
    setIsLoggedIn(false);
    setMobileMenuOpen(false);
    handleMenuNavigation("/", "home");
  };

  // 處理登入
  const handleLogin = async () => {
    console.log("登入表單提交:", projectNameValue);

    setLoginLoading(true);

    try {
      if (!projectNameValue) {
        message.error('請輸入訂單編號');
        return;
      }

      // 🎯 清理輸入值
      const projectName = projectNameValue.trim();

      if (!projectName) {
        message.error('請輸入有效的訂單編號');
        return;
      }

      // 直接使用 queryClient 查詢需求單
      const result = await queryClient.fetchQuery({
        queryKey: ['os_item', projectName],
        queryFn: () => osApi.getItemById(projectName),
        staleTime: 0, // 強制重新查詢
      });

      if (result?.data) {
        // 需求單存在，登入成功
        const memberInfo = {
          projectName: projectName,
          loginTime: new Date().toLocaleString("zh-TW"),
        };
        localStorage.setItem("memberInfo", JSON.stringify(memberInfo));

        // 顯示成功訊息
        message.success("登入成功！即將跳轉到會員專區...");

        // 關閉模態框
        setLoginModalVisible(false);
        setProjectNameValue(""); // 🎯 清空 state
        setIsLoggedIn(true);

        // 延遲跳轉到會員頁面
        setTimeout(() => {
          window.location.href = "/member";
        }, 1000);
      } else {
        message.error("找不到對應的需求單，請檢查您的需求單編號是否正確！");
      }
    } catch (error: any) {
      console.error("登入驗證失敗:", error);

      // 根據錯誤類型顯示不同的錯誤訊息
      if (error?.response?.status === 404) {
        message.error("找不到對應的需求單，請檢查您的需求單編號是否正確！");
      } else if (error?.response?.status === 403) {
        message.error("無權限訪問此需求單！");
      } else {
        message.error("登入失敗，請稍後再試或聯繫客服！");
      }
    } finally {
      setLoginLoading(false);
    }
  };

  // 處理菜單導航
  const handleMenuNavigation = (path: string, key: string) => {
    setSelectedKey(key);
    if (isMobile) setMobileMenuOpen(false);

    // 如果當前路徑與目標路徑不同，才進行導航
    if (window.location.pathname !== path) {
      window.location.href = path;
    }
  };

  // 菜單項目
  const menuItems = [
    {
      key: "home",
      label: "首頁",
      onClick: () => handleMenuNavigation("/", "home"),
    },
    {
      key: "about",
      label: "關於我們",
      onClick: () => handleMenuNavigation("/about", "about"),
    },
    {
      key: "news",
      label: "最新消息",
      onClick: () => handleMenuNavigation("/news", "news"),
    },
    {
      key: "combo",
      label: "服務方案",
      onClick: () => handleMenuNavigation("/combo", "combo"),
    },
    {
      key: "join",
      label: "加入我們",
      onClick: () => handleMenuNavigation("/join", "join"),
    },
    // 根據登入狀態顯示不同的菜單項目
    isLoggedIn
      ? {
        key: "member",
        label: "會員專區",
        onClick: () => handleMenuNavigation("/member", "member"),
      }
      : {
        key: "login",
        label: "會員登入",
        onClick: () => {
          if (isMobile) setMobileMenuOpen(false);

          // 🎯 檢查 localStorage 中是否有訂單編號，自動填入 state
          const memberInfo = JSON.parse(localStorage.getItem("memberInfo") || '{}');
          if (memberInfo.projectName) {
            setProjectNameValue(memberInfo.projectName);
          }

          setLoginModalVisible(true);
        },
      },
    // 如果已登入，顯示登出選項
    ...(isLoggedIn
      ? [
        {
          key: "logout",
          label: "會員登出",
          onClick: handleLogout,
        },
      ]
      : []),
  ];

  return (
    <Spin spinning={loading} tip="載入中..." size="large">
      <Layout style={{ minHeight: "100vh" }}>
        {/* 桌面版側邊欄 - 只有當 isDesktop 為 true 時才渲染 */}
        {isDesktop && (
          <Sider
            width={'20%'}
            style={{
              height: "100vh",
              minWidth: "300px",
              position: "sticky",
              top: 0,
              left: 0,
              bottom: 0,
              zIndex: 1000,
              background: colorBgContainer,
              borderRight: "1px solid #f0f0f0",
              fontFamily: "Noto Serif TC",
            }}
            className="text-center border-r border-[#f0f0f0] shadow-xl"
          >
            {/* Logo 區域 */}
            <Space
              direction="vertical"
              size={8}
              style={{ margin: "16px" }}
            >
              {companyInfo.logo && (
                <Avatar
                  size={64}
                  src={companyInfo.logo}
                />
              )}
              <h2 className="text-[32px] text-center font-bold tracking-widest">
                {companyInfo.name}
              </h2>
            </Space>

            {/* 導航菜單 */}
            <Menu
              mode="inline"
              selectedKeys={[selectedKey]}
              items={menuItems}
              style={{
                border: "none",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                fontFamily: "Noto Serif TC",
                gap: "16px",
                padding: "0 16px",
              }}
              className="text-[24px] font-[600] text-center [&_.ant-menu-item]:!p-0"
            />
          </Sider>
        )}

        <Layout className="[&_.ant-layout-content]:!p-0 h-full">
          {/* 移動端頂部導航 - 只有當 isMobile 為 true 時才渲染 */}
          {isMobile && (
            <Header
              style={{
                padding: "0 16px",
                background: colorBgContainer,
                borderBottom: "1px solid #f0f0f0",
                position: "sticky",
                width: "100%",
                zIndex: 1000,
                top: 0,
                height: "64px",
                lineHeight: "64px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  height: "64px",
                  width: "100%",
                  position: "relative",
                }}
              >
                {/* 漢堡菜單按鈕 */}
                <div
                  style={{ display: "flex", alignItems: "center", zIndex: 10 }}
                >
                  <Button
                    type="text"
                    icon={<MenuOutlined />}
                    onClick={() => setMobileMenuOpen(true)}
                    style={{ fontSize: "18px", height: "40px", width: "40px" }}
                  />
                </div>

                {/* Logo 和公司名稱 - 絕對定位置中 */}
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    zIndex: 5,
                  }}
                  onClick={() => handleMenuNavigation("/", "home")}
                >
                  {companyInfo.logo && (
                    <Avatar size={32} src={companyInfo.logo} />
                  )}
                  <Title
                    level={5}
                    style={{ margin: 0, lineHeight: "1" }}
                    className="font-noto-serif tracking-wider !text-[24px] !font-bold"
                  >
                    {companyInfo.name}
                  </Title>
                </div>
              </div>
            </Header>
          )}

          {/* 手機版 Drawer 導航菜單 - 只有當 isMobile 為 true 時才渲染 */}
          {isMobile && (
            <Drawer
              title={
                <div className="flex items-center justify-center gap-3">
                  {companyInfo.logo && (
                    <Avatar size={40} src={companyInfo.logo} />
                  )}
                  <Title
                    level={5}
                    style={{ margin: 0 }}
                    className="font-noto-serif tracking-widest"
                  >
                    {companyInfo.name}
                  </Title>
                </div>
              }
              placement="left"
              closable={true}
              onClose={() => setMobileMenuOpen(false)}
              open={mobileMenuOpen}
              width={280}
              styles={{
                header: {
                  borderBottom: "1px solid #f0f0f0",
                  padding: "16px 24px",
                },
                body: {
                  padding: "0",
                },
              }}
            >
              <Menu
                mode="vertical"
                selectedKeys={[selectedKey]}
                items={menuItems}
                style={{
                  border: "none",
                  padding: 0,
                  fontFamily: "Noto Serif TC"
                }}
                className="p-0 text-base font-medium text-center"
              />
            </Drawer>
          )}

          {/* 主內容區域 */}
          <Content style={getContentStyles()}>
            <div className="flex">
              {/* 左側主內容 */}
              {mode === "default" && (
                <div className="w-full">
                  <div
                    style={{
                      background: colorBgContainer,
                      borderRadius: borderRadiusLG,
                      overflow: "hidden",
                      marginBottom: 24,
                    }}
                  >
                    <Carousel
                      autoplay
                      style={{ width: "100%" }}
                      dots={{ className: "dots-bottom" }}
                    >
                      {bannerImages.map((url, index) => (
                        <div key={index}>
                          <div className="aspect-[1180/595] relative">
                            <img
                              src={url}
                              alt={`banner${index + 1}`}
                              className="object-cover w-full h-full"
                            />
                          </div>
                        </div>
                      ))}
                    </Carousel>
                  </div>

                  <div
                    style={{
                      minHeight: 360,
                      background: colorBgContainer,
                      borderRadius: borderRadiusLG,
                    }}
                    className="p-2 md:p-6 flex flex-col justify-center items-center gap-4"
                  >
                    {children}
                  </div>
                </div>
              )}

              {mode === "no-banner" && (
                <div className="w-full">
                  {children}
                </div>
              )}

              {mode === "fullscreen" && (
                <div className="w-full h-full md:h-[100vh]">
                  {children}
                </div>
              )}
            </div>

          </Content>

          {/* Footer - 全屏模式下不顯示 */}
          {mode !== "fullscreen" && (
            <Footer style={{ background: "#EAE6D9", textAlign: "center" }}>
              <div className="flex flex-col items-center gap-4">
                <Space direction="vertical" align="center">
                  <Space>
                    {companyInfo.logo && (
                      <Avatar size={54} src={companyInfo.logo} />
                    )}
                    <Title level={3} style={{ margin: 0 }}>
                      {companyInfo.name}
                    </Title>
                  </Space>
                  <Text style={{ fontSize: "16px" }}>{serviceDescription}</Text>
                </Space>

                {/* 社交媒體鏈接 */}
                <Space size="middle">
                  {platforms.map((platform) => (
                    <a
                      key={platform.id}
                      href={platform.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        shape="circle"
                        size="large"
                        style={{
                          background: "white",
                          border: "none",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        className="hover:opacity-75 transition-opacity"
                      >
                        <img
                          src={
                            platform.type === "Youtube"
                              ? "/yt.png"
                              : platform.type === "Tiktok"
                                ? "/tiktok.png"
                                : platform.type === "Instagram"
                                  ? "/Instagram.png"
                                  : platform.type === "Facebook"
                                    ? "/Facebook.png"
                                    : ""
                          }
                          alt={platform.type}
                          style={{ height: "25px" }}
                        />
                      </Button>
                    </a>
                  ))}
                </Space>

                {/* 版權信息 */}
                <div
                  style={{
                    width: "100%",
                    borderTop: "1px solid #ccc",
                    paddingTop: "16px",
                    marginTop: "16px",
                  }}
                >
                  <Text type="secondary">
                    ©2024 {companyInfo.name}. All rights reserved
                  </Text>
                </div>
              </div>
            </Footer>
          )}
        </Layout>
      </Layout>

      {/* 會員登入模態框 */}
      <Modal
        open={loginModalVisible}
        onCancel={() => {
          if (!loginLoading) {
            setLoginModalVisible(false);
            setProjectNameValue(""); // 🎯 清空 state
          }
        }}
        footer={null}
        width={512}
        centered
        closable={!loginLoading}
        maskClosable={!loginLoading}
        styles={{
          mask: {
            backgroundColor: "rgba(0, 0, 0, 0.25)",
          },
          content: {
            borderRadius: "12px",
            padding: "36px 28px",
          },
        }}
      >
        <div className="flex flex-col items-center gap-2">
          {/* Logo 和標題 */}
          <div className="flex flex-col items-center gap-2">
            {companyInfo.logo && <Avatar size={80} src={companyInfo.logo} />}
            <Title level={3} className="mb-4" style={{ color: "#3E3D3D" }}>
              會員登入
            </Title>
          </div>

          {/* 表單內容 */}
          <Flex vertical align="center" className="w-full gap-2">
            <div className="flex flex-col w-full">
              {/* 訂單編號欄位 */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-[#434343] px-1">
                  訂單編號
                </label>
                <Input
                  placeholder="請輸入訂單編號"
                  size="large"
                  value={projectNameValue}
                  onChange={(e) => setProjectNameValue(e.target.value)}
                  onPressEnter={() => !loginLoading && handleLogin()}
                />
              </div>

              {/* 上次填寫的訂單按鈕 / 忘記鏈接 */}
              <div className="flex justify-between">
                <Button
                  type="link"
                  onClick={() => {
                    const memberInfo = JSON.parse(localStorage.getItem("memberInfo") || '{}');
                    if (memberInfo.projectName) {
                      setProjectNameValue(memberInfo.projectName);
                    } else {
                      message.info("沒有找到上次填寫的訂單編號");
                    }
                  }}
                  className="p-0 !text-[#6D6D6D]"
                >
                  使用上次填寫的訂單編號
                </Button>
                <Button
                  type="link"
                  onClick={() => message.info("請聯繫客服協助找回專案資訊")}
                  className="p-0 !text-[#6D6D6D]"
                >
                  忘記訂單編號
                </Button>
              </div>

              {/* 登入按鈕 */}
              <Button
                type="primary"
                size="large"
                block
                loading={loginLoading}
                onClick={handleLogin}
                style={{
                  background: "#D7862A",
                  borderColor: "#D7862A",
                  fontSize: "16px",
                  fontWeight: 600,
                  marginTop: 16
                }}
              >
                {loginLoading ? "驗證中..." : "登入"}
              </Button>
            </div>

            {/* 回到首頁鏈接 */}
            <Button
              type="link"
              disabled={loginLoading}
              onClick={() => {
                if (!loginLoading) {
                  setLoginModalVisible(false);
                  setProjectNameValue(""); // 🎯 清空 state
                  handleMenuNavigation("/", "home");
                }
              }}
              style={{
                color: loginLoading ? "#ccc" : "#007AFF"
              }}
            >
              回到首頁
            </Button>
          </Flex>
        </div>
      </Modal>
    </Spin>
  );
}
